import React, { useCallback, type ReactElement } from 'react';
import { graphql, useFragment } from 'react-relay';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { SECTION_ITEM_FILTERS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { MENU_ID_FILTERS } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import {
	L1_MENU_ID,
	type Nav4DragAndDropMenuItemProps,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { Nav4ExpandableMenuItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-expandable-menu-item/index.tsx';
import { getTestId } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/get-test-id/index.tsx';
import { useSectionItemAnalytics } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/use-section-item-analytics/index.tsx';
import { useConfigurableMessageAndIconMap } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/use-configurable-message-and-icon-map/index.tsx';
// eslint-disable-next-line jira/import/file-extension-required
import { componentWithFG } from '@jira/platform__utils-feature-gates-gate-component';
import { fg } from '@atlassian/jira-feature-gating';
import {
	ExpandableMenuItemContent,
	ExpandableMenuItemTrigger,
} from '@atlaskit/side-nav-items/expandable-menu-item';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { Nav4FiltersContent } from '../../../common/ui/content/Nav4FiltersContent.tsx';
import { PACKAGE_NAME, TEAM_NAME } from '../../../common/constants.tsx';
import { Footer } from '../../../common/ui/footer/index.tsx';
import { Header } from '../../../common/ui/header/index.tsx';

import type {
	Nav4FiltersForLanding$key,
	Nav4FiltersForLanding$data,
} from './__generated__/Nav4FiltersForLanding.graphql';

type Nav4FiltersForLandingProps = Nav4DragAndDropMenuItemProps & {
	actionsOnHover: ReactElement;
	isExpanded: boolean;
	queryRef: Nav4FiltersForLanding$key;
};

export function Nav4FiltersForLanding(props: Nav4FiltersForLandingProps): React.JSX.Element {
	const { queryRef } = props;
	const { formattedMessage, icon } = useConfigurableMessageAndIconMap(L1_MENU_ID.FILTERS);
	const sendSectionItemAnalyticsEvent = useSectionItemAnalytics();

	// eslint-disable-next-line @atlassian/relay/use-fragment-naming, @atlassian/relay/use-fragment-naming
	const data = useFragment<Nav4FiltersForLanding$key>(
		graphql`
			fragment Nav4FiltersForLanding on Query
			@argumentDefinitions(skipOrderQuery: { type: "Boolean!", defaultValue: false }) {
				...Nav4FiltersContent
				filtersOrder: settings_navigationCustomisation(
					entityAri: $entityAriFilters
					ownerAri: $ownerAri
				) @skip(if: $skipOrderQuery) {
					...Nav4FiltersContent_navigationCustomisation
				}
			}
		`,
		// eslint-disable-next-line @atlassian/relay/use-fragment-naming
		queryRef,
	);

	const handleExpansionToggle = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				if (hasExpanded) {
					sendSectionItemAnalyticsEvent({
						menuId: L1_MENU_ID.FILTERS,
						sectionItem: SECTION_ITEM_FILTERS,
					});
				}
			}
		},
		[sendSectionItemAnalyticsEvent],
	);

	const menuId = MENU_ID_FILTERS;

	return (
		<Nav4FiltersForLandingInner
			{...props}
			formattedMessage={formattedMessage}
			handleExpansionToggle={handleExpansionToggle}
			icon={icon}
			menuId={menuId}
			queryRef={data}
		/>
	);
}

const Nav4FiltersForLandingInner = componentWithFG(
	'navx-global-side-nav',
	Nav4FiltersForLandingGSN,
	Nav4FiltersForLandingNav4,
);

type InnerProps = Omit<Nav4FiltersForLandingProps, 'queryRef'> & {
	formattedMessage: string;
	handleExpansionToggle: (hasExpanded: boolean) => void;
	icon: JSX.Element;
	menuId: string;
	queryRef: Nav4FiltersForLanding$data;
};

function Nav4FiltersForLandingGSN(props: InnerProps): React.JSX.Element {
	const {
		actionsOnHover,
		draggableRef,
		dropIndicator,
		formattedMessage,
		handleExpansionToggle,
		hasDragIndicator,
		icon,
		isDragging,
		isExpanded,
		menuId,
		onExpandFlyoutToggle,
		queryRef,
		visualContentRef,
	} = props;

	const handleExpansionToggle2 = useCallback(
		(hasExpanded: boolean) => {
			handleExpansionToggle(hasExpanded);
			onExpandFlyoutToggle?.(hasExpanded);
		},
		[onExpandFlyoutToggle, handleExpansionToggle],
	);

	return (
		<Nav4ExpandableMenuItem
			dropTargetRef={visualContentRef}
			dropIndicator={dropIndicator}
			isDragging={isDragging}
			isExpanded={isExpanded}
			menuId={menuId}
			onExpansionToggle={handleExpansionToggle2}
		>
			<ExpandableMenuItemTrigger
				actionsOnHover={actionsOnHover}
				aria-label={formattedMessage}
				elemBefore={icon}
				hasDragIndicator={hasDragIndicator}
				interactionName="nav4-sidebar-filters-landing"
				isDragging={isDragging}
				ref={draggableRef}
				testId={getTestId(menuId)}
			>
				{formattedMessage}
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				<Header />
				<UFOSegment name="nav4-sidebar-filters-landing">
					<JSErrorBoundary
						id="nav4-sidebar-filters-landing"
						packageName={PACKAGE_NAME}
						teamName={TEAM_NAME}
						fallback="flag"
					>
						<Nav4FiltersContent
							queryRef={queryRef}
							navigationCustomisationFragment={
								fg('hot-305275-fix-filters-reordering-gsn')
									? (queryRef.filtersOrder ?? undefined)
									: undefined
							}
						/>
					</JSErrorBoundary>
				</UFOSegment>
				<Footer />
			</ExpandableMenuItemContent>
		</Nav4ExpandableMenuItem>
	);
}

function Nav4FiltersForLandingNav4(props: InnerProps) {
	const {
		actionsOnHover,
		formattedMessage,
		handleExpansionToggle,
		icon,
		isExpanded,
		menuId,
		queryRef,
	} = props;

	return (
		<Nav4ExpandableMenuItem
			isExpanded={isExpanded}
			menuId={menuId}
			onExpansionToggle={handleExpansionToggle}
		>
			<ExpandableMenuItemTrigger
				interactionName="nav4-sidebar-filters-landing"
				actionsOnHover={actionsOnHover}
				// Prevent double-up of accessible name that occurs in icon + text situation.
				aria-label={formattedMessage}
				elemBefore={icon}
				testId={getTestId(menuId)}
			>
				{formattedMessage}
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				<Header />
				<UFOSegment name="nav4-sidebar-filters-landing">
					<JSErrorBoundary
						id="nav4-sidebar-filters-landing"
						packageName={PACKAGE_NAME}
						teamName={TEAM_NAME}
						fallback="flag"
					>
						<Nav4FiltersContent
							queryRef={queryRef}
							navigationCustomisationFragment={queryRef.filtersOrder ?? undefined}
						/>
					</JSErrorBoundary>
				</UFOSegment>
				<Footer />
			</ExpandableMenuItemContent>
		</Nav4ExpandableMenuItem>
	);
}
